/**
 * AudioGenerationPage Component
 *
 * Main page for AI audio generation.
 * Refactored into smaller, focused components.
 */

import React, { useEffect, useCallback, useRef, memo } from 'react';
import { ScriptPreviewModal } from '../../components/ScriptPreviewModal';
import { LoginModal } from '../../components/auth';
import { ToastContainer } from '../../components/Toast';
import { useAudioGeneration } from '../../hooks/useAudioGeneration';
import { useScriptPreview } from '../../hooks/useScriptPreview';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../hooks/useTranslation';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { AudioHero } from './AudioHero';
import { AudioConfigSection } from './AudioConfigSection';
import { AudioProgress } from './AudioProgress';
import { AudioError } from './AudioError';
import { AudioResults } from './AudioResults';
import { AudioFeatures } from './AudioFeatures';
import type {
  AudioGenerationRequest,
  ScriptPreviewRequest,
  ScriptEditOverrides,
} from '../../types';

export const AudioGenerationPage: React.FC = memo(function AudioGenerationPage() {
  const { t } = useTranslation();
  const {
    isGenerating,
    progressData,
    audioUrl,
    audioMetadata,
    refreshResult,
    error,
    sanitizedError,
    generateAudio,
    reset,
  } = useAudioGeneration();
  const {
    isLoading: isPreviewLoading,
    preview,
    error: previewError,
    isModalOpen,
    generatePreview,
    regeneratePreview,
    closeModal,
    reset: resetPreview,
  } = useScriptPreview();
  const { toasts, showToast, showSanitizedErrorToast, removeToast } = useToast();
  const { isLoginModalOpen, loginMessage, closeLoginModal, requireAuth } = useRequireAuth();

  // Store the last config used for preview so we can use it for generation
  const lastPreviewConfigRef = useRef<AudioGenerationRequest | null>(null);

  const handleSubmit = useCallback(
    async (config: AudioGenerationRequest) => {
      // Require authentication before generating audio
      requireAuth(async () => {
        showToast('info', 'Starting Audio Generation', 'Your audio is being generated...', 3000);
        await generateAudio(config);
      }, t('auth.loginRequired.audio'));
    },
    [requireAuth, showToast, generateAudio, t]
  );

  const handlePreviewScript = useCallback(
    async (config: AudioGenerationRequest & { topic: string }) => {
      // Require authentication before previewing script
      requireAuth(async () => {
        // Store config for later use
        lastPreviewConfigRef.current = config;

        // Create preview request
        const previewRequest: ScriptPreviewRequest = {
          generation_type: 'audio',
          topic: config.topic,
          duration_seconds: config.duration_seconds,
          language: config.language,
          llm_provider: config.llm_provider,
          llm_model: config.llm_model,
        };

        await generatePreview(previewRequest);
      }, t('auth.loginRequired.audio'));
    },
    [generatePreview, requireAuth, t]
  );

  const handleApproveScript = useCallback(
    async (scriptText: string, overrides?: ScriptEditOverrides) => {
      // Close the modal
      closeModal();

      // Use the stored config with the approved/edited script
      if (lastPreviewConfigRef.current) {
        const config: AudioGenerationRequest = {
          ...lastPreviewConfigRef.current,
          mode: 'text', // Switch to text mode since we have the script
          narration_text: scriptText,
          // Apply voice overrides from edit mode
          ...(overrides?.tts_provider && { tts_provider: overrides.tts_provider }),
          ...(overrides?.voice_id && { voice_id: overrides.voice_id }),
        };

        showToast('info', 'Starting Audio Generation', 'Using approved script...', 3000);
        await generateAudio(config);
      }
    },
    [closeModal, generateAudio, showToast]
  );

  const handleRegeneratePreview = useCallback(async () => {
    await regeneratePreview();
  }, [regeneratePreview]);

  const handleClosePreviewModal = useCallback(() => {
    closeModal();
    resetPreview();
  }, [closeModal, resetPreview]);

  const handleReset = useCallback(() => {
    reset();
    resetPreview();
    lastPreviewConfigRef.current = null;
    showToast('info', 'Reset Complete', 'Form has been reset', 2000);
  }, [reset, resetPreview, showToast]);

  // Show error toast when generation fails — use sanitized path when available
  useEffect(() => {
    if (error) {
      if (sanitizedError) {
        showSanitizedErrorToast('Generation Failed', sanitizedError);
      } else {
        showToast('error', 'Generation Failed', error);
      }
    }
  }, [error, sanitizedError, showToast, showSanitizedErrorToast]);

  useEffect(() => {
    if (previewError) {
      showToast(
        'error',
        t('scriptPreview.toastErrorTitle', { defaultValue: 'Script preview failed' }),
        previewError
      );
    }
  }, [previewError, showToast, t]);

  // Show success toast when generation completes. Keyed on the job, not on its
  // links: a link renewed after it expired is the same result, not a new one.
  const finishedJobId = audioUrl ? audioMetadata?.generation_id : undefined;
  useEffect(() => {
    if (finishedJobId) {
      showToast('success', 'Audio Ready!', 'Your audio has been generated successfully');
    }
  }, [finishedJobId, showToast]);

  const showContent = !isGenerating && !audioUrl;
  const showResults = audioUrl && audioMetadata && !isGenerating;
  const showError = error && !isGenerating;

  return (
    <main className="page-container" data-testid="main-content">
      {/* Hero Section */}
      {showContent && <AudioHero />}

      {/* Main Content Area */}
      <div className="space-y-6">
        {/* Configuration Form */}
        {showContent && (
          <AudioConfigSection
            onSubmit={handleSubmit}
            onPreviewScript={handlePreviewScript}
            disabled={isGenerating}
            isPreviewLoading={isPreviewLoading}
          />
        )}

        {/* Progress Indicator */}
        {isGenerating && <AudioProgress progress={progressData} />}

        {/* Error Display */}
        {showError && (
          <AudioError error={error} sanitizedError={sanitizedError} onReset={handleReset} />
        )}

        {/* Audio Player */}
        {showResults && (
          <AudioResults
            url={audioUrl}
            metadata={audioMetadata}
            refreshResult={refreshResult}
            onReset={handleReset}
          />
        )}

        {/* Features Section */}
        {showContent && <AudioFeatures />}
      </div>

      {/* Script Preview Modal */}
      <ScriptPreviewModal
        isOpen={isModalOpen}
        onClose={handleClosePreviewModal}
        preview={preview}
        isLoading={isPreviewLoading}
        error={previewError}
        onApprove={handleApproveScript}
        onRegenerate={handleRegeneratePreview}
        generationType="audio"
        currentConfig={
          lastPreviewConfigRef.current
            ? {
                tts_provider: lastPreviewConfigRef.current.tts_provider,
                voice_id: lastPreviewConfigRef.current.voice_id,
                language: lastPreviewConfigRef.current.language,
              }
            : undefined
        }
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Login Modal (auth required for actions) */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} message={loginMessage} />
    </main>
  );
});

export default AudioGenerationPage;
